import { observer } from 'mobx-react-lite';
import { ContentHead, Page, RoundedPaper } from '../../lib/components';
import { AddButton } from '../company/settings/shared/addButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CalendarScheduleIcon, ExchangeIcon, InfoIcon, PlusIcon } from '../../lib/icons';
import { BonusOverviewTable } from '../../lib/components/tables/bonus/bonus';
import { FeedbackAlert } from '../../lib/components/atomic/feedback/alert';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useStore } from '../../hooks/useStore';
import { useListBonuses } from '../../queries/bonus';
import Util from '../../misc/util';
import { Drawer } from '../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../lib/components/atomic/typography/InfoSection';

export const Bonus = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);

  const { data: bonusData, isLoading, isError } = useListBonuses(companyStore.company?.id);

  const { currentYearTotal, nextYearTotal } = useMemo(() => {
    if (!bonusData) return { currentYearTotal: 0, nextYearTotal: 0 };

    const currentTotal = (bonusData?.results ?? []).reduce(
      (sum, bonus) => sum + (bonus.annual_amount ?? 0),
      0
    );
    const nextTotal = (bonusData?.results ?? []).reduce(
      (sum, bonus) => sum + (bonus.annual_amount_next_year ?? 0),
      0
    );

    return { currentYearTotal: currentTotal, nextYearTotal: nextTotal };
  }, [bonusData]);

  const handleClickCreate = () => navigate('/actions/bonus/create_bonus/1');

  return (
    <Page className="flex justify-center">
      <Stack spacing={2} className="max-w-[1800px] w-full px-4 h-full">
        <RoundedPaper className="box-border flex flex-col">
          <Box className="flex flex-col">
            <ContentHead
              headline={t('bonus:management')}
              showActions={true}
              renderActions={() => (
                <AddButton
                  text={t('bonus:createBonus')}
                  startIcon={<PlusIcon />}
                  onClick={handleClickCreate}
                />
              )}
            />
            <Box className="p-8">
              <Box className="flex gap-7">
                <Box className="flex gap-4 items-start border border-solid border-grey-200 rounded-2xl p-4 flex-1">
                  <ExchangeIcon className="text-4xl text-primary bg-grey-50 rounded-lg p-2" />
                  <Box>
                    <Typography variant="body2">
                      {t('bonus:totalVolume') + ' ' + new Date().getFullYear()}
                    </Typography>
                    <Typography variant="h5" className="font-semibold mb-1">
                      {isLoading ? '...' : Util.formatCurrency(currentYearTotal)}
                    </Typography>
                  </Box>
                </Box>

                <Box className="flex gap-4 items-start border border-solid border-grey-200 rounded-2xl p-4 flex-1">
                  <CalendarScheduleIcon className="text-4xl text-primary bg-grey-50 rounded-lg p-2" />
                  <Box>
                    <Typography variant="body2">
                      {t('bonus:totalVolume') + ' ' + (new Date().getFullYear() + 1)}
                    </Typography>
                    <Typography variant="h5" className="font-semibold mb-1">
                      {isLoading ? '...' : Util.formatCurrency(nextYearTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </RoundedPaper>
        <RoundedPaper className="flex-grow box-border flex flex-col overflow-auto min-h-0">
          <Box className="flex-grow flex flex-col">
            <Box className="sticky top-0 z-10 bg-white">
              <ContentHead
                headline={t('bonus:overview')}
                showActions={true}
                renderActions={() => <InfoIcon onClick={() => setShowInfoDrawer(true)} />}
              />
            </Box>
            <Box className="flex flex-grow overflow-y-hidden">
              <Box className="flex-grow p-8 overflow-y-auto">
                {isError ? (
                  <FeedbackAlert
                    severity="error"
                    title={t('bonus:loadFailedTitle')}
                    description={t('bonus:loadFailedText')}
                  />
                ) : (
                  <BonusOverviewTable bonusData={bonusData?.results ?? []} isLoading={isLoading} />
                )}
              </Box>
            </Box>
          </Box>
          <Drawer
            label={t('bonus:bonusDrawer:title')}
            open={showInfoDrawer}
            closeDrawer={() => setShowInfoDrawer(false)}
          >
            <div className="flex flex-col gap-y-6">
              <InfoSection content={t('bonus:bonusDrawer:texts', { returnObjects: true })} />
            </div>
          </Drawer>
        </RoundedPaper>
      </Stack>
    </Page>
  );
});
